import PropTypes from 'prop-types'
import clsx from 'clsx'

const StatusBanner = ({
  icon,
  title,
  subtitle,
  action,
  centerAction = false,
  children,
  className,
  dataTestId = 'status-banner',
}) => (
  <div
    className={clsx('w-full p-4 rounded-2xl bg-white border border-grey-90', className)}
    data-testid={dataTestId}
  >
    <div className="flex items-start justify-between gap-4">
      <div className="flex items-start phone:gap-2 gap-1 min-w-0 flex-1">
        {icon && (
          <span
            data-testid="status-banner-icon"
            className="shrink-0 inline-flex items-center justify-center p-1 text-grey-50 [&_svg]:w-5 [&_svg]:h-5 [&_svg]:block"
          >
            {icon}
          </span>
        )}
        <div className="flex flex-col gap-0.5 min-w-0 py-1">
          {title && <div className="text-sm leading-5 font-semibold text-grey-30">{title}</div>}
          {subtitle && (
            <div data-testid="status-banner-subtitle" className="text-xs leading-5 text-grey-50">
              {subtitle}
            </div>
          )}
        </div>
      </div>
      {action && (
        // `centerAction` centres the action against the whole text block rather
        // than aligning it to the first line, which is how the ported precast
        // banner reads it: the action belongs to the banner rather than to its
        // title, and against a two-line title-plus-date it otherwise looks like
        // it is hanging off the top. Opt-in, so the banners that already ship
        // keep the top alignment they have.
        <div
          data-testid="status-banner-action-wrapper"
          className={clsx('shrink-0', centerAction && 'self-center')}
        >
          {action}
        </div>
      )}
    </div>
    {children}
  </div>
)

const NOTE_VARIANTS = {
  neutral: 'var(--color-grey-98)',
  info: 'var(--color-blue-98)',
  error: 'var(--color-red-98)',
  warning: 'var(--color-yellow-98)',
  success: 'var(--color-green-98)',
}

const Action = ({ onClick, children, dataTestId = 'status-banner-action' }) => (
  <button
    type="button"
    className="py-1 px-2 bg-transparent border-0 outline-none text-theme-50 text-sm font-bold no-underline cursor-pointer hover:bg-transparent focus:bg-transparent active:bg-transparent focus:outline-none active:outline-none"
    onClick={onClick}
    data-testid={dataTestId}
  >
    {children}
  </button>
)

const Note = ({ variant = 'neutral', children, className }) => (
  <div
    data-testid="status-banner-note"
    data-variant={variant}
    className={clsx('mt-3 ml-9 py-3 px-4 rounded-lg max-phone:ml-0 text-sm leading-5', className)}
    style={{ backgroundColor: NOTE_VARIANTS[variant] }}
  >
    {children}
  </div>
)

const NoteHeading = ({ children }) => <div className="font-bold text-grey-10">{children}</div>
const NoteBody = ({ children }) => (
  <div className="mt-1 whitespace-pre-wrap wrap-break-word text-grey-40">{children}</div>
)
const NoteMeta = ({ children }) => <div className="mt-1 text-grey-50">{children}</div>

Note.Heading = NoteHeading
Note.Body = NoteBody
Note.Meta = NoteMeta

StatusBanner.Action = Action
StatusBanner.Note = Note

StatusBanner.propTypes = {
  icon: PropTypes.node,
  title: PropTypes.node,
  subtitle: PropTypes.node,
  action: PropTypes.node,
  centerAction: PropTypes.bool,
  children: PropTypes.node,
  className: PropTypes.string,
  dataTestId: PropTypes.string,
}

Action.propTypes = {
  onClick: PropTypes.func,
  children: PropTypes.node,
  dataTestId: PropTypes.string,
}

Note.propTypes = {
  variant: PropTypes.oneOf(Object.keys(NOTE_VARIANTS)),
  children: PropTypes.node,
  className: PropTypes.string,
}

NoteHeading.propTypes = { children: PropTypes.node }
NoteBody.propTypes = { children: PropTypes.node }
NoteMeta.propTypes = { children: PropTypes.node }

export default StatusBanner
